import axios, { AxiosInstance } from "axios";
import { CORPORATE_BACKOFFICE_PATHS, HMI_PATHS, MADERA_BACKOFFICE_PATHS } from "constants/constants";
import { getClusterIdHMI, getMachineCenterIdHMI, getUser } from "utilities/storageUtilities";

/**
 * Detects the current application context based on the URL pathname.
 * @returns The context identifier: 'corporate-backoffice', 'madera-backoffice', or 'hmi/...'
 */
const getApplicationContext = (): string => {
    const pathname = window.location.pathname.toLowerCase();

    if (pathname.includes(CORPORATE_BACKOFFICE_PATHS.ROOT_PATH.toLowerCase())) {
        const user = getUser();

        if (user) {
            return `corporate-backoffice/user-${user}`;
        }
        return "corporate-backoffice";
    }
    if (pathname.includes(MADERA_BACKOFFICE_PATHS.ROOT_PATH.toLowerCase())) {
        const user = getUser();

        if (user) {
            return `madera-backoffice/user-${user}`;
        }
        return "madera-backoffice";
    }
    if (pathname.includes(HMI_PATHS.ROOT_PATH.toLowerCase())) {
        const machineCenterIdHMI = getMachineCenterIdHMI();
        const clusterIdHMI = getClusterIdHMI();

        if (pathname.includes(HMI_PATHS.DASHBOARD.toLowerCase()) && machineCenterIdHMI) {
            return `hmi/machine-center-${machineCenterIdHMI.machine_center_id}`;
        }
        if (pathname.includes(HMI_PATHS.CLUSTER.toLowerCase()) && clusterIdHMI) {
            return `hmi/cluster-${clusterIdHMI}`;
        }
        return "hmi";
    }

    return "unknown";
};

/**
 * Generates the value for the X-Madera-Context header for frontend HTTP requests.
 * Format: {context}/{version}
 * Examples:
 * - corporate-backoffice/1.1.1
 * - madera-backoffice/1.1.1
 * - hmi/1.1.1
 */
const getMaderaContextHeaderValue = (): string => {
    const context = getApplicationContext();
    const version = process.env.REACT_APP_VERSION || "0.0.0";
    return `${context}/${version}`;
};

/**
 * Configured axios instance with dynamic X-Madera-Context header.
 * The header is determined on each request to ensure it reflects the current context.
 */
const axiosInstance: AxiosInstance = axios.create();

axiosInstance.interceptors.request.use(
    (config) => {
        config.headers = config.headers || {};
        config.headers["X-Madera-Context"] = getMaderaContextHeaderValue();
        return config;
    },
    (error) => {
        return Promise.reject(error);
    },
);

export default axiosInstance;
